/**
 * InfoCard Component
 * 
 * A card component for displaying information sections with a title and icon.
 * Used for System Information, System Assignment, etc.
 */

import React from 'react';
import { Box, Paper, Typography, Divider } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { colors, spacing, typography as typographyTokens } from '../../tokens';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface InfoCardProps {
  /** Card title displayed in the header */
  title: string;
  /** Optional icon to display next to the title */
  icon?: React.ReactNode;
  /** Card content */
  children: React.ReactNode;
  /** Optional helper text or note displayed below the title */
  note?: string;
  /** Additional styles */
  sx?: SxProps<Theme>;
  /** Whether to show header divider */
  showDivider?: boolean;
  /** Card padding */
  padding?: number;
  /** Optional actions to display on the right side of the header */
  actions?: React.ReactNode;
}

export const InfoCard: React.FC<InfoCardProps> = ({
  title,
  icon,
  children,
  note,
  sx,
  showDivider = false,
  padding = spacing.lg,
  actions,
}) => (
  <Paper
    elevation={0}
    sx={{
      width: '100%',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {/* Header */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        px: padding / 8,
        pt: padding / 8,
        pb: showDivider ? 2 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {icon && (
          <Box
            sx={{
              color: colors.neutral[500],
              display: 'flex',
              alignItems: 'center',
              '& svg': {
                fontSize: 22,
              },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="h4"
          sx={{
            fontFamily: typographyTokens.fontFamily.display,
            fontWeight: typographyTokens.fontWeight.semibold,
            fontSize: typographyTokens.fontSize.lg,
            color: colors.neutral[900],
          }}
        >
          {title}
        </Typography>
      </Box>
      {actions && <Box sx={{ display: 'flex', alignItems: 'center' }}>{actions}</Box>}
    </Box>

    {/* Note/Helper text */}
    {note && (
      <Box
        sx={{
          mx: padding / 8,
          mb: 2,
          p: 1.5,
          borderRadius: 1,
          backgroundColor: colors.neutral[50],
          border: `1px solid ${colors.neutral[200]}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <InfoOutlinedIcon
          sx={{
            fontSize: 18,
            color: colors.neutral[500],
            mt: 0.25,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: colors.neutral[600],
            fontSize: typographyTokens.fontSize.sm,
          }}
        >
          {note}
        </Typography>
      </Box>
    )}

    {showDivider && (
      <Divider sx={{ mx: padding / 8 }} />
    )}

    {/* Content */}
    <Box
      sx={{
        p: padding / 8,
        pt: showDivider ? 2 : 1,
      }}
    >
      {children}
    </Box>
  </Paper>
);

export default InfoCard;

